import { AxiosRequestConfig } from 'axios';
import { MutableRefObject, useEffect, useMemo, useRef } from 'react';
import useSWR, {
  ConfigInterface,
  SWRInfiniteConfigInterface,
  useSWRInfinite,
} from 'swr';
import api from '@services/api';

const handleAbortSignal = (
  controller: MutableRefObject<AbortController | undefined>,
) => {
  if (controller?.current) {
    controller?.current?.abort();
  }
};

export default function useSwr<Data = any, Error = any>(
  url: string | null,
  requestConfig: AxiosRequestConfig | null | undefined = undefined,
  swrConfig?: ConfigInterface<Data, Error> & { cancelPrevious?: boolean },
  keyToSave?: string,
) {
  const controller = useRef<AbortController>();

  const { cancelPrevious = true, ...restSwrConfig } = swrConfig || {};

  useEffect(() => () => handleAbortSignal(controller), []);

  const swrURL = useMemo(() => {
    if (requestConfig?.params || requestConfig?.data) {
      return url
        ? [
            url,
            JSON.stringify({
              ...requestConfig?.params,
              ...requestConfig?.data,
              keyToSave,
            }),
          ]
        : null;
    }

    return url;
  }, [requestConfig?.data, requestConfig?.params, url, keyToSave]);

  const { data, error, mutate, revalidate, isValidating } = useSWR<Data, Error>(
    swrURL,
    async urlToFetch => {
      if (cancelPrevious) {
        handleAbortSignal(controller);
      }
      controller.current = new AbortController();
      const response = await api({
        url: urlToFetch,
        signal: controller?.current?.signal,
        method: requestConfig?.method ?? 'get',
        ...requestConfig,
      });
      return response.data;
    },
    {
      errorRetryCount: 2,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      ...restSwrConfig,
    },
  );

  const isLoading = !data && !error && isValidating;

  return { data, error, mutate, revalidate, isLoading };
}

export function useSwrInfinite<Data = any, Error = any>(
  url: string,
  linesPerPage: number,
  params: AxiosRequestConfig | undefined = undefined,
  config?: SWRInfiniteConfigInterface<Data, Error>,
  pageOffset?: number,
) {
  const urlFn = useMemo(() => {
    return (index: number) => {
      const searchParams = new URLSearchParams('');
      // @ts-ignore
      searchParams.set('page', index + (pageOffset ?? 0));
      if (linesPerPage || linesPerPage === 0) {
        // @ts-ignore
        searchParams.set('linesPerPage', linesPerPage);
      }

      let urlWithParams = `${url}?${searchParams}`;
      Object.entries(params?.params).forEach(([key, value]: any) => {
        if (value || value === false) {
          urlWithParams += `&${key}=${value}`;
        }
      });

      return urlWithParams;
    };
  }, [linesPerPage, pageOffset, params?.params, url]);

  const {
    data: dataInfinite,
    mutate,
    size,
    setSize,
    error,
  } = useSWRInfinite(
    urlFn,
    async urlToFetch => {
      const response = await api.get<any>(urlToFetch);
      return response.data.body.serviceRequests as any;
    },
    config,
  );

  const isLoadingInitialData = useMemo(
    () => !dataInfinite && !error,
    [dataInfinite, error],
  );
  const isLoadingMore = useMemo(
    () =>
      isLoadingInitialData ||
      (size > 0 &&
        dataInfinite &&
        typeof dataInfinite[size - 1] === 'undefined'),
    [dataInfinite, isLoadingInitialData, size],
  );
  const isEmpty = useMemo(() => {
    return (dataInfinite?.[0] as any)?.length === 0;
  }, [dataInfinite]);
  const isReachingEnd = useMemo(
    () =>
      isEmpty ||
      (dataInfinite &&
        (dataInfinite[dataInfinite.length - 1] as any)?.length < linesPerPage),
    [dataInfinite, isEmpty, linesPerPage],
  );

  return {
    data: dataInfinite,
    mutate,
    size,
    setSize,
    isLoadingInitialData,
    isLoadingMore,
    isEmpty,
    isReachingEnd,
  };
}
